import { FormEvent, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../features/auth/AuthContext';
import { config } from '../lib/config';
import { BrandLogo } from '../components/BrandLogo';
import { ThemeToggle } from '../components/ThemeToggle';
import { PasswordField } from '../components/PasswordField';

export function SignInPage(): JSX.Element {
  const { challenge, login, completeNewPassword, isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (isLoggedIn) {
    return <Navigate to="/clients" replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (challenge?.challenge === 'NEW_PASSWORD_REQUIRED') {
        await completeNewPassword(email || challenge.email, newPassword);
      } else {
        const result = await login(email, password);
        if (result.challenge) return;
      }
      navigate('/clients', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed');
    } finally {
      setLoading(false);
    }
  }

  const isNewPassword = challenge?.challenge === 'NEW_PASSWORD_REQUIRED';

  return (
    <div className="login-screen">
      <div className="login-card">
        <div className="login-card__lighting">
          <ThemeToggle />
        </div>
        <BrandLogo />
        <p className="login-card__eyebrow">SenseAI HQ</p>
        <h1>{isNewPassword ? 'Set your password' : 'Staff sign-in'}</h1>
        <p className="login-card__copy">
          {isNewPassword
            ? 'Finish your invite by choosing a new password, then open the registry console.'
            : 'Staff Cognito access to Portal global tables. Invites arrive by email with a temporary password.'}
        </p>
        <form className="login-form" onSubmit={handleSubmit}>
          <label>
            <span>Email</span>
            <input
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              type="email"
              required
              autoComplete="username"
            />
          </label>
          {isNewPassword ? (
            <PasswordField
              label="New password"
              value={newPassword}
              onChange={setNewPassword}
              minLength={config.passwordMinLength}
              autoComplete="new-password"
            />
          ) : (
            <PasswordField
              label="Password"
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
            />
          )}
          {error ? <div className="form-error">{error}</div> : null}
          <button className="primary-button" disabled={loading} type="submit" aria-busy={loading}>
            {loading ? 'Please wait…' : isNewPassword ? 'Set password' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}
